import { z } from "zod";
import { adminRoleSchema } from "./admin-rbac.js";

/**
 * Shared DTO cho admin lifecycle & access review (US-AE1.F7-0036 / @UC9005).
 * Một nguồn sự thật cho api (S01 — endpoint lifecycle) và admin web (S04 — rà soát
 * + actions), tránh drift shape ở 2 nơi (DRY). Tái dùng `AdminRole` từ admin-rbac (#8),
 * KHÔNG định nghĩa lại role-list.
 *
 * Quan hệ với #8: GET /admin/members (US-AE1.F2-0002) đã trả list item
 * `{ adminMemberId, email, role, status }` (chỉ tồn tại trong api service, KHÔNG ở
 * contracts). Task này (AC2 rà soát account "ngủ") THÊM `lastActiveAt` cho view
 * lifecycle ⇒ định nghĩa summary tại đây, GIỮ NGUYÊN tên field của #8 + bổ sung
 * `lastActiveAt` (align, không drift).
 *
 * BẢO MẬT (invariants):
 * - DTO CHỈ mang field cần cho UI — KHÔNG lộ passwordHash/totp/secret/token (C2/C3).
 * - Envelope list khớp quy ước repo `{ data, pageInfo: { hasNext, nextCursor } }`
 *   (đối xứng admin-search · admin-members list). KHÔNG dùng {items, nextCursor}.
 * - Break-glass: TTL do SERVER kiểm soát (60m từ config `ADMIN_BREAK_GLASS_TTL_MINUTES`,
 *   index §6b DoR), KHÔNG nhận từ client. grantedRole = SUPER_ADMIN do server gán
 *   (chỉ SUPER_ADMIN hiện tại được cấp). Client CHỈ gửi { adminMemberId, reason }.
 */

/** Hằng status admin member (đối xứng cột AdminMember.status — #8). */
const ADMIN_MEMBER_STATUS_DISABLED = "disabled" as const;

/**
 * List item cho view rà soát lifecycle: id/email/role/status (như #8) + lastActiveAt.
 * `lastActiveAt` nullable: account chưa từng đăng nhập ⇒ null (rà soát "ngủ").
 */
export const adminMemberSummarySchema = z.object({
  adminMemberId: z.string(),
  email: z.string(),
  role: adminRoleSchema,
  status: z.string(),
  lastActiveAt: z.string().nullable(),
});

export type AdminMemberSummary = z.infer<typeof adminMemberSummarySchema>;

/** Envelope list khớp quy ước repo `{ data, pageInfo }` (cursor opaque, hasNext). */
export const listAdminMembersResponseSchema = z.object({
  data: z.array(adminMemberSummarySchema),
  pageInfo: z.object({
    hasNext: z.boolean(),
    nextCursor: z.string().nullable(),
  }),
});

export type ListAdminMembersResponse = z.infer<typeof listAdminMembersResponseSchema>;

/**
 * Response AC1 offboard: deactivate member ⇒ status='disabled' + số session đã revoke.
 * `revokedSessionCount` ∈ ℤ≥0 (revoke MỌI session admin đó).
 */
export const deactivateAdminMemberResponseSchema = z.object({
  adminMemberId: z.string(),
  status: z.literal(ADMIN_MEMBER_STATUS_DISABLED),
  revokedSessionCount: z.number().int().min(0),
});

export type DeactivateAdminMemberResponse = z.infer<
  typeof deactivateAdminMemberResponseSchema
>;

/** Response revoke session (không deactivate): chỉ số session đã thu hồi. */
export const revokeAdminSessionsResponseSchema = z.object({
  adminMemberId: z.string(),
  revokedSessionCount: z.number().int().min(0),
});

export type RevokeAdminSessionsResponse = z.infer<
  typeof revokeAdminSessionsResponseSchema
>;

/**
 * Body break-glass grant (AC3): CHỈ adminMemberId(uuid) + reason(min 1).
 * TTL + grantedRole do SERVER áp (index §6b), KHÔNG client-supplied ⇒ object strip
 * field thừa (zod mặc định strip key không khai báo).
 */
export const breakGlassGrantRequestSchema = z.object({
  adminMemberId: z.string().uuid(),
  reason: z.string().min(1),
});

export type BreakGlassGrantRequest = z.infer<typeof breakGlassGrantRequestSchema>;

/**
 * Response break-glass grant: grantedRole = SUPER_ADMIN (literal — server gán),
 * expiresAt = thời điểm tự hết hạn (TTL từ config, ISO string).
 */
export const breakGlassGrantResponseSchema = z.object({
  grantId: z.string(),
  adminMemberId: z.string(),
  grantedRole: z.literal("SUPER_ADMIN"),
  expiresAt: z.string(),
});

export type BreakGlassGrantResponse = z.infer<typeof breakGlassGrantResponseSchema>;

/**
 * AC4 chống tự khoá: SUPER_ADMIN DUY NHẤT tự deactivate ⇒ chặn. Code dùng chung
 * api ↔ UI để hiển thị lỗi nhất quán (không leak chi tiết khác).
 */
export const ADMIN_LAST_SUPER_ADMIN_CODE = "LAST_SUPER_ADMIN" as const;
export const ADMIN_LAST_SUPER_ADMIN_MESSAGE =
  "Không thể vô hiệu hoá SUPER_ADMIN cuối cùng còn hoạt động.";

/**
 * Chặn tự-deactivate (H2 review #18): offboard phải DO ADMIN KHÁC thực hiện — một admin KHÔNG
 * tự vô hiệu hoá chính mình (phòng vệ chiều sâu + nền cho chống tự khoá). Orthogonal với
 * LAST_SUPER_ADMIN (đó là "người cuối"); SELF là "chính mình" — luôn chặn bất kể còn bao nhiêu.
 */
export const ADMIN_SELF_DEACTIVATE_CODE = "SELF_DEACTIVATE" as const;
export const ADMIN_SELF_DEACTIVATE_MESSAGE =
  "Không thể tự vô hiệu hoá tài khoản của chính mình.";
